/**
 * VH-114: ExerciseCaptureBlock — the shared, type-aware capture UI for ONE
 * exercise. Extracted from VH-113's CaptureExercise so it can be reused by both
 * the single-exercise page (`/capture/:token`) and the new whole-session page
 * (`/capture/session/:token`).
 *
 * It branches on the exercise's field template (`per_set`):
 *
 *   • Set-based (strength / tabata / mix …, `per_set=true`) → reps/weight
 *     steppers + weight-notation per set (the VH-113 path → SetResult rows).
 *   • Metric-based (cycling / cardio / hiit …, `per_set=false`) → one input per
 *     template field (e.g. bike = duration, distance, avg_speed), mirroring the
 *     in-app ExerciseCard → SessionExercise.metric_values. NO reps/weight
 *     steppers, NO SetResult rows.
 *
 * The component is presentational: the parent page owns the editable state
 * (`sets` / `metricValues`) and receives changes via the `on*` callbacks.
 * Helper builders below turn a load payload into that state.
 *
 * VH-120: the "Mark as substitute" checkbox was removed — capture no longer
 * owns substitution (the web logging flow's Skip / Substitute does). The
 * read-only substitute *badge* (an exercise substituted on the web) stays.
 */
import { useTranslation } from 'react-i18next'
import { TrophyIcon } from '@heroicons/react/24/outline'
import ReportedBadge from './ReportedBadge'
import { exerciseProgress, isSetReported } from '../../utils/captureProgress'
import { toNumberOrNull, isBlockPerSet, displayNumber } from './exerciseCaptureUtils'
import { useAvgSpeedProposal } from '../../utils/avgSpeed'
import { contentLanguage } from '../../i18n/supported'

// Round to 2 decimals to avoid float drift from repeated +/- 2.5 steps.
function roundStep(n) {
  return Math.round(n * 100) / 100
}

// Build a compact "8–10 @ 60" style hint from the planned targets.
function targetHint(set) {
  const reps =
    set.target_reps_min != null && set.target_reps_max != null
      ? set.target_reps_min === set.target_reps_max
        ? `${set.target_reps_min}`
        : `${set.target_reps_min}–${set.target_reps_max}`
      : set.target_reps_min != null
      ? `${set.target_reps_min}`
      : null
  const weight = displayNumber(set.target_weight)
  return { reps, weight }
}

// VH-113 A1: step sizes. Reps move in whole units; weight in 2.5 (kg/lb) plates.
const REPS_STEP = 1
const WEIGHT_STEP = 2.5

// VH-113 A1: a number input flanked by explicit − / + steppers. Native phone
// spinners are unreliable, so we render real buttons AND keep type="number" so
// hardware-keyboard up/down still works. Clamps at 0.
function StepperInput({ value, onChange, step, inputMode, decLabel, incLabel, disabled = false }) {
  const stepBy = (delta) => {
    const current = toNumberOrNull(value)
    const base = current === null ? 0 : current
    const next = Math.max(0, roundStep(base + delta))
    onChange(next)
  }
  return (
    <div className="flex items-stretch">
      <button
        type="button"
        aria-label={decLabel}
        onClick={() => stepBy(-step)}
        disabled={disabled}
        className="flex-none w-9 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-lg font-semibold text-gray-700 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        −
      </button>
      <input
        type="number"
        inputMode={inputMode}
        min="0"
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        // VH-278: on a 390 px phone the field between the two steppers was ~50 px
        // wide and showed 102.5 as "102." — the number that decides what goes on
        // the bar. Less side padding, narrower steppers, and no native spinner
        // (the − / + buttons replace it) leave room for 5 characters: 102.5, 87.25.
        className="w-full min-w-0 border-y px-0.5 py-2 text-center text-base tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
      />
      <button
        type="button"
        aria-label={incLabel}
        onClick={() => stepBy(step)}
        disabled={disabled}
        className="flex-none w-9 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-lg font-semibold text-gray-700 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        +
      </button>
    </div>
  )
}

export default function ExerciseCaptureBlock({
  block,
  sets = [],
  metricValues = {},
  locked = false,
  showTitle = false,
  onSetChange,
  onMetricChange,
}) {
  const { t, i18n } = useTranslation()
  const perSet = isBlockPerSet(block)
  // VH-140: what of this exercise is already stored. Derived from the payload —
  // a set holding actual values IS a reported set — so no new backend state.
  const progress = exerciseProgress(block)
  // Set numbers the SERVER says hold stored values. The `sets` prop is edit
  // state and is pre-filled with targets, so it cannot answer this.
  const reportedSetNumbers = new Set(
    (block?.sets ?? []).filter(isSetReported).map((s) => s.set_number)
  )
  // VH-208: set numbers the SERVER has marked as read from a photo and out of
  // line with their siblings. Shown here, confirmed in the app — the sheet is
  // for entering what you did, not for adjudicating what a photo read.
  // VH-207: the prefilled weight came from the location variant and has not
  // been lifted here yet. Marked so an aim never reads as a proven number.
  const targetSetNumbers = new Set(
    (block?.sets ?? []).filter((s) => s.is_target).map((s) => s.set_number)
  )
  const unconfirmedSetNumbers = new Set(
    (block?.sets ?? []).filter((s) => s.needs_confirmation).map((s) => s.set_number)
  )
  const lang = contentLanguage(i18n?.language)

  // Propose avg. speed from distance + duration (bike/e-bike/cardio …).
  const { speedKey, isProposed, handleChange: handleMetricChange } = useAvgSpeedProposal({
    fields: block.metric_fields,
    values: metricValues,
    onChange: onMetricChange,
    enabled: !locked,
  })

  const setTypeLabel = (type) => t(`capture.setType.${type}`, { defaultValue: type })
  const fieldLabel = (field) =>
    lang === 'de' ? field.label_de || field.label_en || field.key : field.label_en || field.label_de || field.key

  // VH-173: a skipped exercise is one you did NOT do, so it must not still
  // look like something to fill in. Reported from UAT: the Skip button worked
  // but "it shows still editable and available for reporting", which makes the
  // click look like it did nothing. Reuses the existing `locked` path rather
  // than adding a second notion of read-only.
  const isSkipped = Boolean(block.is_skipped)
  const inputsLocked = locked || isSkipped

  return (
    <div className="bg-surface-container rounded-lg shadow-sm p-4">
      {/* Exercise title (whole-session page) */}
      {showTitle && (
        <div className="mb-3 flex items-start justify-between gap-2">
          <h2 className="text-base font-bold text-gray-900">{block.exercise_name}</h2>
          <ReportedBadge
            state={progress.state}
            reported={progress.reported}
            total={progress.total}
            className="shrink-0"
          />
          {isSkipped && (
            <span
              className="flex-none rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700"
              data-testid="capture-skipped-badge"
            >
              {t('captureSession.skipped', { defaultValue: 'Skipped' })}
            </span>
          )}
          {block.is_substitute && (
            <span className="flex-none rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              {t('programs.print.substituteFlag')}
            </span>
          )}
        </div>
      )}

      {/* VH-277: the heaviest weight lifted so far. The app's exercise card has always
          shown it; this sheet is where it decides what goes on the bar. */}
      {perSet && block.personal_best?.max_weight > 0 && (
        <p
          className="mb-3 inline-flex items-center gap-1 rounded border border-yellow-200 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700"
          data-testid="capture-personal-best"
        >
          <TrophyIcon className="h-3.5 w-3.5 text-yellow-500" aria-hidden="true" />
          {block.personal_best.max_weight_reps
            ? t('capture.personalBest', {
                weight: block.personal_best.max_weight,
                reps: block.personal_best.max_weight_reps,
              })
            : t('capture.personalBestWeightOnly', { weight: block.personal_best.max_weight })}
        </p>
      )}

      {perSet ? (
        // ── Set-based: reps/weight steppers + notation per set ──
        <div className="space-y-3">
          {sets.map((set, index) => {
            const hint = targetHint(set)
            return (
              <div
                key={`${set.set_number}-${set.set_type}`}
                data-testid={`set-row-${set.set_number}${reportedSetNumbers.has(set.set_number) ? '-reported' : ''}`}
                className={`rounded-lg border p-3 ${
                  unconfirmedSetNumbers.has(set.set_number)
                    ? 'border-l-4 border-l-amber-500 border-amber-300 bg-amber-50'
                    : isSetReported(set)
                    ? 'border-l-4 border-l-success-600 border-gray-200'
                    : 'border-gray-200'
                }`}
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">
                    {t('capture.setLabel', { number: set.set_number })}
                  </span>
                  <span className="flex items-center gap-2">
                    {unconfirmedSetNumbers.has(set.set_number) && (
                      <span
                        data-testid={`set-unconfirmed-${set.set_number}`}
                        className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                        title={t('programs.unconfirmed.explain')}
                      >
                        {t('programs.unconfirmed.badge')}
                      </span>
                    )}
                    {targetSetNumbers.has(set.set_number) && (
                      <span
                        data-testid={`set-target-${set.set_number}`}
                        className="rounded border border-dashed border-primary-400 px-2 py-0.5 text-xs font-semibold italic text-primary-700"
                        title={t('variants.targetExplain')}
                      >
                        {t('variants.target')}
                      </span>
                    )}
                    {/* VH-140: which of the five pull-up sets are already in. */}
                    {reportedSetNumbers.has(set.set_number) && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success-600">
                        {t('captureProgress.setReported')}
                      </span>
                    )}
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {setTypeLabel(set.set_type)}
                    </span>
                  </span>
                </div>

                {/* VH-114 B3: WEIGHT first, then REPS — matches the paper sheet
                    column order (kg | reps) so screen and print stay consistent. */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      {t('capture.weight')}
                      {hint.weight && (
                        <span className="ml-1 font-normal text-gray-400">
                          {t('capture.targetHint', { value: hint.weight })}
                        </span>
                      )}
                    </label>
                    <StepperInput
                      value={set.actual_weight}
                      inputMode="decimal"
                      step={WEIGHT_STEP}
                      decLabel={t('capture.decrease')}
                      incLabel={t('capture.increase')}
                      disabled={inputsLocked}
                      onChange={(v) => onSetChange?.(index, 'actual_weight', v)}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      {t('capture.reps')}
                      {hint.reps && (
                        <span className="ml-1 font-normal text-gray-400">
                          {t('capture.targetHint', { value: hint.reps })}
                        </span>
                      )}
                    </label>
                    <StepperInput
                      value={set.actual_reps}
                      inputMode="numeric"
                      step={REPS_STEP}
                      decLabel={t('capture.decrease')}
                      incLabel={t('capture.increase')}
                      disabled={inputsLocked}
                      onChange={(v) => onSetChange?.(index, 'actual_reps', v)}
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-xs font-medium text-gray-600">{t('capture.notation')}</label>
                  <input
                    type="text"
                    value={set.weight_notation}
                    placeholder={t('capture.notationPlaceholder')}
                    disabled={inputsLocked}
                    onChange={(e) => onSetChange?.(index, 'weight_notation', e.target.value)}
                    className="w-full px-3 py-2 text-base focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        // ── Metric-based: one input per template field (bike = duration, …) ──
        <div className="space-y-3">
          {(block.metric_fields || []).map((field) => {
            const target = block.metric_targets?.[field.key]
            return (
              <div key={field.key}>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {fieldLabel(field)}
                  {field.unit && <span className="ml-1 font-normal text-gray-400">({field.unit})</span>}
                  {target != null && target !== '' && (
                    <span className="ml-1 font-normal text-gray-400">
                      {t('capture.targetHint', { value: target })}
                    </span>
                  )}
                  {field.key === speedKey && isProposed && (
                    <span className="ml-1 font-normal text-gray-400">
                      {t('capture.calculatedHint', 'calculated — edit to override')}
                    </span>
                  )}
                </label>
                <input
                  type={field.type === 'text' ? 'text' : 'number'}
                  inputMode={field.type === 'text' ? undefined : 'decimal'}
                  min={field.type === 'text' ? undefined : '0'}
                  value={metricValues[field.key] ?? ''}
                  disabled={inputsLocked}
                  placeholder={field.unit || '-'}
                  onChange={(e) => handleMetricChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 text-base focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
